import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { CREATE_PROJECT, UPDATE_PROJECT } from '../../graphql/mutations';
import { GET_TINYCMS_PAGES } from '../../graphql';
import { showInkToast } from '../cms/InkToast';
import { useTinycmsSites } from '../cms/sites';
import {
  calendarDate,
  campaignStart,
  Channel,
  channelKind,
  channelMark,
  channelName,
  dateValue,
  isPersonalProfile,
  postTime,
  REACH_CHANNELS,
  ReachPost,
  shortDate,
  toDate,
  PUBLISHING_OFF_WORDS,
  publishingOff,
} from './reach';

/**
 * New / Edit campaign for a dated campaign (Prototype v14, Reach → Campaigns):
 * name, brief, Starts and Ends, the channels it posts to, and where its posts
 * link — one of the site's pages, a page the agent drafts, another address, or
 * nothing (the articles themselves).
 *
 * Changing the dates moves posts that haven't gone out and now fall outside
 * them to Drafts (the server does it on save); the form says how many first.
 */

type LandingMode = 'page' | 'agent' | 'url' | 'none';

export interface EditableCampaign {
  id: string;
  name: string;
  campaignStory?: string | null;
  deadline?: string | null;
  startDate?: string | null;
  createdAt?: string | null;
  landingPageUrl?: string | null;
  landingPageStatus?: string | null;
  channels?: Array<{ id: string }>;
  recentPosts?: ReachPost[];
}

const LANDING: Array<[LandingMode, string]> = [
  ['page', 'One of your pages'],
  ['agent', 'Agent makes one'],
  ['url', 'Another address'],
  ['none', 'None'],
];
const LANDING_NOTE: Record<LandingMode, string> = {
  page: 'Posts link here. Edit the page itself in Pages.',
  agent:
    'Your agent drafts a page with a sign-up form from the brief. It waits for your approval like any page.',
  url: 'Posts link to this address. Sign-ups there won’t show in campaign results.',
  none: 'Posts link to the articles they’re about.',
};

const dayInput = (v?: string | null) => {
  const d = calendarDate(v) || (v ? toDate(v) : null);
  return d ? dateValue(d) : '';
};
/** The Starts field: the same day the list and detail show (campaignStart). */
const startInput = (c: { startDate?: string | null; createdAt?: string | null }) => {
  const d = campaignStart(c);
  return d ? dateValue(d) : '';
};
const addDays = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return dateValue(d);
};

export function CampaignEditor({
  campaign,
  onClose,
  onSaved,
}: {
  campaign: EditableCampaign | null;
  onClose: () => void;
  onSaved: (id: string | null) => void;
}) {
  const { accountId } = useAccount();
  const { sites } = useTinycmsSites();
  const siteUrl = (sites.find((s) => s.accountId === accountId)?.siteUrl || '').replace(/\/+$/, '');
  const { data: chData } = useQuery<{ socialProfiles: Channel[] }>(REACH_CHANNELS, {
    variables: { accountId },
    skip: !accountId,
  });
  const { data: pageData } = useQuery(GET_TINYCMS_PAGES, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const channels = useMemo(
    () => (chData?.socialProfiles || []).filter((c) => c.isActive),
    [chData]
  );
  const pages: Array<{ id: string; title: string; path: string }> = useMemo(
    () => pageData?.tinycmsPages || [],
    [pageData]
  );
  const [create, { loading: creating }] = useMutation(CREATE_PROJECT);
  const [update, { loading: updating }] = useMutation(UPDATE_PROJECT);
  const busy = creating || updating;

  const pageOf = (url?: string | null) =>
    url && siteUrl && url.startsWith(siteUrl)
      ? pages.find((p) => p.path === (url.slice(siteUrl.length) || '/')) || null
      : null;
  const initialMode = (): LandingMode => {
    const s = (campaign?.landingPageStatus || '').toLowerCase();
    if (s === 'page' || s === 'agent' || s === 'url' || s === 'none') return s;
    return campaign?.landingPageUrl ? 'url' : 'none';
  };

  const [name, setName] = useState(campaign?.name || '');
  const [brief, setBrief] = useState(campaign?.campaignStory || '');
  const [start, setStart] = useState(campaign ? startInput(campaign) : addDays(0));
  const [end, setEnd] = useState(campaign ? dayInput(campaign.deadline) : addDays(14));
  const [picked, setPicked] = useState<Set<string>>(
    () => new Set((campaign?.channels || []).map((c) => c.id))
  );
  const [mode, setMode] = useState<LandingMode>(initialMode);
  const [pagePath, setPagePath] = useState<string | null>(null);
  const [url, setUrl] = useState(campaign?.landingPageUrl || '');
  const [error, setError] = useState('');

  // The page the campaign links to now, once the pages are in.
  const currentPage = pageOf(campaign?.landingPageUrl);
  const chosenPath = pagePath ?? currentPage?.path ?? pages[0]?.path ?? '';

  const toggle = (id: string) =>
    setPicked((p) => {
      const next = new Set(p);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Posts not yet out that the new dates leave outside: they move to Drafts.
  const moved = useMemo(() => {
    if (!campaign || !start || !end) return [];
    const from = new Date(`${start}T00:00:00`).getTime();
    const to = new Date(`${end}T23:59:59.999`).getTime();
    return (campaign.recentPosts || []).filter((p) => {
      if (p.reachStatus === 'posted' || p.socialPublishDate) return false;
      const t = postTime(p)?.getTime();
      return t != null && (t < from || t > to);
    });
  }, [campaign, start, end]);

  const urlOk = /^https?:\/\/\S+\.\S+/i.test(url.trim());
  const block = !name.trim()
    ? 'Give it a name.'
    : !start || !end
      ? 'Pick the dates.'
      : end < start
        ? 'The end date is before the start.'
        : // A new campaign needs somewhere to post. One saved without channels
          // (allowed before) can still be renamed or re-briefed (QA round 12).
          !picked.size && !campaign
          ? 'Pick at least one channel.'
          : mode === 'page' && !chosenPath
            ? 'Pick a page.'
            : mode === 'url' && !urlOk
              ? 'Enter the page’s full address (https://…).'
              : '';

  const save = async () => {
    if (block || !accountId) return;
    setError('');
    const landingPageUrl =
      mode === 'page' ? `${siteUrl}${chosenPath}` : mode === 'url' ? url.trim() : null;
    const fields = {
      name: name.trim(),
      campaignStory: brief.trim() || null,
      startDate: start,
      deadline: end,
      channelIds: [...picked],
      landingPageUrl,
      landingPageStatus: mode,
    };
    try {
      if (campaign) {
        await update({ variables: { id: campaign.id, input: fields } });
        showInkToast(
          `Saved.${moved.length ? ` ${moved.length} ${moved.length === 1 ? 'post' : 'posts'} moved to Drafts.` : ''}`
        );
        onSaved(campaign.id);
      } else {
        const { data } = await create({
          variables: { input: { accountId, kind: 'campaign', status: 'planning', ...fields } },
        });
        showInkToast(
          `${fields.name} saved as a draft. Add posts, then schedule it when you’re ready.`
        );
        onSaved(data?.createProject?.id || null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t save.');
    }
  };

  return (
    <div
      className="ink-modal-back"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        className="ink-modal aoe"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ce-title"
        style={{ width: 'min(640px, 100%)' }}
      >
        <div className="ink-modal-title" id="ce-title">
          {campaign ? 'Edit campaign' : 'New campaign'}
        </div>
        <p className="ink-modal-copy">
          {campaign
            ? 'Changes apply to posts that haven’t gone out yet.'
            : 'Set it up yourself. It stays a draft until you schedule it.'}
        </p>

        <div className="aoe-form">
          <label className="ink-site-label">
            Name
            <input
              className="ink-site-input"
              value={name}
              autoFocus
              maxLength={200}
              placeholder="Name"
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label className="ink-site-label">
            <span>
              Brief <span className="ink-muted">(optional)</span>
            </span>
            <textarea
              className="ink-site-input"
              rows={3}
              value={brief}
              maxLength={2000}
              placeholder="Brief"
              onChange={(e) => setBrief(e.target.value)}
            />
          </label>
          <div className="aoe-two">
            <label className="ink-site-label">
              Starts
              <input
                className="ink-site-input"
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </label>
            <label className="ink-site-label">
              Ends
              <input
                className="ink-site-input"
                type="date"
                value={end}
                min={start || undefined}
                onChange={(e) => setEnd(e.target.value)}
                aria-invalid={Boolean(start && end && end < start)}
              />
            </label>
          </div>
          {moved.length ? (
            <span className="aoe-help" style={{ color: 'var(--ink-amber)' }}>
              {moved.length} {moved.length === 1 ? 'post falls' : 'posts fall'} outside these dates
              ({moved.map((p) => shortDate(postTime(p))).join(', ')}). Saving moves{' '}
              {moved.length === 1 ? 'it' : 'them'} to Drafts so nothing posts outside the campaign.
            </span>
          ) : null}

          <div className="ink-site-label">
            Channels
            {channels.length ? (
              <div className="ce-chans">
                {channels.map((c) => {
                  const personal = isPersonalProfile(c.platform, c.profileId);
                  return (
                    <label
                      className="ce-chan"
                      key={c.id}
                      data-on={picked.has(c.id) ? 'true' : undefined}
                      data-off={publishingOff(c) ? 'true' : undefined}
                    >
                      <input
                        type="checkbox"
                        checked={picked.has(c.id)}
                        disabled={publishingOff(c) && !picked.has(c.id)}
                        onChange={() => toggle(c.id)}
                      />
                      <span className="rc-mark rc-mark-sm">{channelMark(c.platform)}</span>
                      <span className="chp-name">
                        <span>{c.profileName || c.profileUsername || channelName(c.platform)}</span>
                        <span
                          className="ink-muted"
                          style={personal ? { color: 'var(--ink-amber)' } : undefined}
                        >
                          {channelName(c.platform)} ·{' '}
                          {publishingOff(c)
                            ? PUBLISHING_OFF_WORDS
                            : channelKind(c.platform, c.profileId)}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <span className="ink-muted">No channels connected yet. Connect one in Channels.</span>
            )}
          </div>

          <div className="ink-site-label">
            Landing page
            <span className="ink-seg" style={{ alignSelf: 'flex-start', flexWrap: 'wrap' }}>
              {LANDING.map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  data-active={mode === key}
                  onClick={() => setMode(key)}
                >
                  {label}
                </button>
              ))}
            </span>
            {mode === 'page' ? (
              pages.length ? (
                <select
                  className="ink-site-input"
                  value={chosenPath}
                  aria-label="Landing page"
                  onChange={(e) => setPagePath(e.target.value)}
                >
                  {pages.map((p) => (
                    <option key={p.id} value={p.path}>
                      {p.title} · {p.path}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="ink-muted aoe-help">
                  No pages yet. Make one in Website → Pages.
                </span>
              )
            ) : mode === 'url' ? (
              <input
                className="ink-site-input"
                type="url"
                value={url}
                placeholder="https://example.com/page"
                aria-label="Landing page address"
                onChange={(e) => setUrl(e.target.value)}
              />
            ) : null}
            <span className="ink-muted aoe-help">{LANDING_NOTE[mode]}</span>
          </div>
        </div>

        {error ? (
          <p className="aoe-help" style={{ color: 'var(--ink-red)', marginTop: 12 }}>
            {error}
          </p>
        ) : null}
        <div className="ink-modal-actions">
          {block ? (
            <span className="ink-muted wp-block">{block}</span>
          ) : !picked.size ? (
            <span className="ink-muted wp-block">
              No channels yet. Its posts need one before they can go out.
            </span>
          ) : null}
          <button type="button" className="ink-btn ink-btn-quiet" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ink-btn ink-btn-primary"
            disabled={Boolean(block) || busy}
            onClick={() => void save()}
          >
            {busy ? 'Saving…' : campaign ? 'Save changes' : 'Create campaign'}
          </button>
        </div>
      </div>
    </div>
  );
}
